import type { CalculatorPackage, CalculatorPackageRow } from './calculatorPackages';
import type { CalculatorSettings, Currency, FXRates } from '@/pages/EU6Calculator/EU6Calculator.types';
import { parsePackageNumber } from '@/pages/EU6Calculator/calculatorSettingsFromPackage';
import { packageBuyingPointForApi } from '@/pages/EU6Calculator/calculatorSettingsFromPackage';
import type { TradingDeal } from '@/api/admin';

type RawRecord = Record<string, unknown>;

function pickNumber(...candidates: unknown[]): number | undefined {
  for (const value of candidates) {
    const n = parsePackageNumber(value);
    if (n != null) return n;
  }
  return undefined;
}

function pickString(...candidates: unknown[]): string | undefined {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return undefined;
}

function nestedSettings(raw: RawRecord): RawRecord | undefined {
  const settings = raw.settings;
  return settings && typeof settings === 'object' ? (settings as RawRecord) : undefined;
}

function normalizeRows(rows: unknown): CalculatorPackageRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const r = row as RawRecord;
    return {
      country: String(r.country ?? ''),
      channel: String(r.channel ?? ''),
      publisher: String(r.publisher ?? ''),
      format: String(r.format ?? ''),
      deliveryPercentage: Number(r.deliveryPercentage ?? r.delivery_percentage) || 0,
      rowCurrency: String(r.rowCurrency ?? r.row_currency ?? 'GBP'),
      buyCPM: Number(r.buyCPM ?? r.buy_cpm) || 0,
      dspFeePercentage: Number(r.dspFeePercentage ?? r.dsp_fee_percentage) || 0,
      dspName: r.dspName ? String(r.dspName) : undefined
    };
  });
}

/** Normalize API responses (flat fields, snake_case, or nested `settings`). */
export function normalizeCalculatorPackage(raw: CalculatorPackage): CalculatorPackage {
  const doc = raw as CalculatorPackage & RawRecord;
  const nested = nestedSettings(doc);

  const totalBudget = pickNumber(
    doc.totalBudget,
    doc.total_budget,
    nested?.totalBudget,
    nested?.total_budget
  );
  const sellCpm = pickNumber(doc.sellCpm, doc.sell_cpm, nested?.sellCPM, nested?.sellCpm, nested?.sell_cpm);
  const hardCostsTotal = pickNumber(
    doc.hardCostsTotal,
    doc.hard_costs_total,
    nested?.hardCostsTotal,
    nested?.hard_costs_total
  );
  const tradingDealPercentage = pickNumber(
    doc.tradingDealPercentage,
    doc.trading_deal_percentage,
    nested?.tradingDealPercentage,
    nested?.trading_deal_percentage
  );
  const currency = pickString(doc.currency, nested?.displayCurrency, nested?.currency);
  const fxRaw = doc.fxRates ?? doc.fx_rates ?? nested?.fxRates ?? nested?.fx_rates;

  return {
    ...doc,
    rows: normalizeRows(doc.rows),
    ...(totalBudget != null && { totalBudget }),
    ...(sellCpm != null && { sellCpm }),
    ...(hardCostsTotal != null && { hardCostsTotal }),
    ...(tradingDealPercentage != null && { tradingDealPercentage }),
    ...(currency && { currency }),
    ...(fxRaw && typeof fxRaw === 'object' && { fxRates: fxRaw as CalculatorPackage['fxRates'] })
  };
}

export type CalculatorPackageWriteInput = {
  name: string;
  description?: string;
  channel: string;
  format?: string;
  rows: CalculatorPackageRow[];
  settings: CalculatorSettings;
  fxRates: FXRates;
  tradingDeals?: TradingDeal[];
  blendedMarginPercent: number;
  blendedProfit: number;
  blendedBuyCpm: number;
};

/** Build POST/PUT body — flat fields plus nested `settings` for backend compatibility. */
export function buildCalculatorPackageWriteBody(
  input: CalculatorPackageWriteInput
): Record<string, unknown> {
  const { settings, fxRates, tradingDeals } = input;
  const totalBudget = Number(settings.totalBudget) || 0;
  const sellCpm = Number(settings.sellCPM) || 0;
  const hardCostsTotal = Number(settings.hardCostsTotal) || 0;
  const tradingDealPercentage = Number(settings.tradingDealPercentage) || 0;
  const currency = settings.displayCurrency;
  const buyingPoint = packageBuyingPointForApi(settings, tradingDeals);

  const settingsPayload = {
    totalBudget,
    sellCPM: sellCpm,
    displayCurrency: currency,
    hardCostsTotal,
    tradingDealPercentage,
    buyingPoint,
    fxRates: fxRates as unknown as Record<string, number>
  };

  return {
    name: input.name,
    description: input.description,
    channel: input.channel,
    ...(input.format !== undefined && { format: input.format }),
    rows: input.rows,
    totalBudget,
    total_budget: totalBudget,
    sellCpm,
    sell_cpm: sellCpm,
    hardCostsTotal,
    hard_costs_total: hardCostsTotal,
    tradingDealPercentage,
    trading_deal_percentage: tradingDealPercentage,
    currency,
    buyingPoint,
    fxRates: settingsPayload.fxRates,
    blendedMarginPercent: input.blendedMarginPercent,
    blendedProfit: input.blendedProfit,
    blendedBuyCpm: input.blendedBuyCpm,
    settings: settingsPayload
  };
}
